import { AttributeNames } from "./names.js";
import { AttributeValues } from "./values.js";
const combine = (...configs) => {
  const handlerMap = /* @__PURE__ */ new Map();
  configs.forEach((config) => {
    const handlers = handlerMap.get(config.name) || [];
    handlers.push(config.handler);
    handlerMap.set(config.name, handlers);
  });
  return Array.from(handlerMap.entries()).map(([name, handlers]) => ({
    name,
    handler: (ref, oldValue, newValue) => {
      handlers.forEach((handler2) => handler2(ref, oldValue, newValue));
    }
  }));
};
const resize = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.deprecated.state.RESIZE,
  handler: (ref, _, newValue) => {
    if (newValue === AttributeValues.state.TRUE) {
      const element = ref.element;
      const previousSize = {
        width: element.offsetWidth,
        height: element.offsetHeight
      };
      callback(ref);
      setTimeout(() => {
        const currentSize = {
          width: element.offsetWidth,
          height: element.offsetHeight
        };
        element.dispatchEvent(
          new CustomEvent("component:resize", {
            detail: {
              tagName: element.tagName.toLowerCase(),
              element,
              timestamp: Date.now(),
              previousSize,
              currentSize,
              source: "attribute"
            },
            bubbles: true,
            composed: true
          })
        );
      }, 0);
    }
  }
});
const stateOpen = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.deprecated.state.STATE,
  handler: (ref, oldValue, newValue) => {
    if (newValue === AttributeValues.state.OPENED && oldValue === AttributeValues.state.CLOSED) {
      callback(ref);
    }
  }
});
const stateClosed = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.deprecated.state.STATE,
  handler: (ref, oldValue, newValue) => {
    if (newValue === AttributeValues.state.CLOSED && oldValue === AttributeValues.state.OPENED) {
      callback(ref);
    }
  }
});
const visuallyClosed = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.visual.open,
  handler: (ref, oldValue, newValue) => {
    if (newValue === AttributeValues.state.FALSE && oldValue === AttributeValues.state.TRUE) {
      callback(ref);
    }
  }
});
const visuallyHide = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.layout.hidden,
  handler: (ref, oldValue, newValue) => {
    if (newValue === AttributeValues.state.TRUE && oldValue === AttributeValues.state.FALSE) {
      callback(ref);
    }
  }
});
const visuallyOpen = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.visual.open,
  handler: (ref, oldValue, newValue) => {
    if (newValue === AttributeValues.state.TRUE && oldValue === AttributeValues.state.FALSE) {
      callback(ref);
    }
  }
});
const visuallyPosition = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.layout.position,
  handler: (ref, oldValue, newValue) => {
    if (newValue !== oldValue) {
      const top = newValue ? parseInt(newValue) : null;
      if (top) {
        callback(ref, top);
      } else {
        callback(ref);
      }
    }
  }
});
const visuallyShow = ({
  callback,
  name
}) => ({
  name: name || AttributeNames.layout.hidden,
  handler: (ref, oldValue, newValue) => {
    if (newValue === AttributeValues.state.FALSE && oldValue === AttributeValues.state.TRUE) {
      callback(ref);
    }
  }
});
const observe = {
  resize,
  stateOpen,
  stateClosed,
  visuallyHide,
  visuallyOpen,
  visuallyClosed,
  visuallyPosition,
  visuallyShow
};
const common = {
  /**
   * Resize observer for responsive components
   */
  resize: (callback) => resize({ callback }),
  /**
   * Visual show/hide handlers for modals and overlays
   */
  visualShowHide: (options) => combine(
    ...options.onShow ? [
      visuallyShow({
        callback: options.onShow
      })
    ] : [],
    ...options.onHide ? [
      visuallyHide({
        callback: options.onHide
      })
    ] : []
  ),
  /**
   * Visual open/close handlers for expandable components
   */
  visualToggle: (options) => combine(
    ...options.onOpen ? [
      visuallyOpen({
        callback: options.onOpen
      })
    ] : [],
    ...options.onClose ? [
      visuallyClosed({
        callback: options.onClose
      })
    ] : []
  ),
  /**
   * Standard accordion handlers with open/close transitions
   */
  accordion: () => combine(
    resize({
      callback: (element) => element.events?.open({ hasAnimation: false })
    }),
    visuallyOpen({
      callback: (element) => element.events?.open({ hasAnimation: true })
    }),
    visuallyClosed({
      callback: (element) => element.events?.close({ hasAnimation: true })
    }),
    // Deprecated handlers for backwards compatibility
    stateClosed({
      callback: (element) => element.events?.close({ hasAnimation: true })
    }),
    stateOpen({
      callback: (element) => element.events?.open({ hasAnimation: true })
    })
  )
};
const handler = { combine, observe, common };
export {
  handler
};
//# sourceMappingURL=handler.js.map
